import { useCallback, useEffect, useState } from "react";
import { Button, Input } from "../../../ui";
import { formatSar } from "../../../../utils/billingMoney";
import { adminAdjustAiWalletAPI, adminGetAiWalletAPI, adminListAiWalletEntriesAPI, adminListAiWalletsAPI, adminReverseAiWalletEntryAPI, adminSetAiWalletStatusAPI, newIdempotencyKey } from "../../../../utils/api/aiWallet";
import AiWalletActionModal from "./AiWalletActionModal";
import { STATE_BADGE, formatWalletDate } from "../aiWalletText";

const ENTRY_BADGE = { posted: "badge-success", pending: "badge-warning", void: "badge-neutral" };
const REVERSIBLE = new Set(["TOP_UP", "ADJUSTMENT"]);

// Wallets of every restaurant, and one wallet in depth: balance, open holds,
// the full ledger (with internal references) and the manual actions an admin
// may take. Every action is a new ledger entry with a reason.
const AiWalletAdminWallets = ({ text, lang, access }) => {
  const t = text.admin;
  const canAdjust = Boolean(access?.canAdjustAiWallet);
  const [search, setSearch] = useState("");
  const [lowBalance, setLowBalance] = useState(false);
  const [openId, setOpenId] = useState("");
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [action, setAction] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setList(await adminListAiWalletsAPI({ search, lowBalance: lowBalance ? "true" : "", limit: 50 }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, lowBalance]);

  const loadDetail = useCallback(async (storeId) => {
    setLoading(true);
    setError("");
    try {
      const [wallet, ledger] = await Promise.all([adminGetAiWalletAPI(storeId), adminListAiWalletEntriesAPI(storeId, { limit: 100 })]);
      setDetail(wallet);
      setEntries(ledger.entries || []);
      setSelected(storeId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(loadList, 250);
    return () => clearTimeout(timer);
  }, [loadList]);

  const runAction = async (values) => {
    const storeId = selected;
    if (action.kind === "adjust") await adminAdjustAiWalletAPI(storeId, { direction: values.direction, amountMinor: values.amountMinor, reason: values.reason, idempotencyKey: action.key });
    if (action.kind === "reverse") await adminReverseAiWalletEntryAPI(action.entry._id, { reason: values.reason, allowPartial: values.allowPartial });
    if (action.kind === "status") await adminSetAiWalletStatusAPI(storeId, { status: action.status, reason: values.reason });
    setAction(null);
    await loadDetail(storeId);
  };

  if (selected && detail) {
    const { wallet, ledgerCheck } = detail;
    return (
      <div className="aiw-admin">
        <div>
          <Button size="sm" appearance="ghost" onClick={() => { setSelected(null); setDetail(null); loadList(); }}>
            {t.back}
          </Button>
        </div>
        {error && <div className="billing-error">{error}</div>}
        <div className="aiw-detail-head">
          <div>
            <h3>{wallet.storeName || wallet.storeId}</h3>
            <small>{wallet.storeId}</small>
          </div>
          {canAdjust && (
            <div className="aiw-row-actions">
              <Button appearance="outline" onClick={() => setAction({ kind: "adjust", key: newIdempotencyKey("adj") })}>
                {t.adjust}
              </Button>
              <Button appearance="outline" onClick={() => setAction({ kind: "status", status: wallet.status === "frozen" ? "active" : "frozen" })}>
                {wallet.status === "frozen" ? t.unfreeze : t.freeze}
              </Button>
            </div>
          )}
        </div>
        <div className="aiw-kpis">
          <div className="aiw-kpi">
            <span>{t.colBalance}</span>
            <strong>{formatSar(wallet.balanceMinor, lang)}</strong>
          </div>
          <div className="aiw-kpi">
            <span>{t.colReserved}</span>
            <strong>{formatSar(wallet.reservedMinor, lang)}</strong>
          </div>
          <div className="aiw-kpi">
            <span>{text.colStatus}</span>
            <strong>
              <span className={wallet.status === "frozen" ? "badge-danger" : "badge-success"}>{wallet.status === "frozen" ? text.frozen : t.ruleStatus.active}</span>
            </strong>
          </div>
          <div className="aiw-kpi">
            <span>{t.ledger}</span>
            <strong>
              <span className={ledgerCheck?.ok ? "badge-success" : "badge-danger"}>{ledgerCheck?.ok ? t.ledgerOk : t.ledgerMismatch}</span>
            </strong>
          </div>
        </div>

        {wallet.holds?.length > 0 && (
          <div className="aiw-panel">
            <h4>{t.holds}</h4>
            {wallet.holds.map((hold) => (
              <div key={hold.chargeId} className="aiw-muted">
                {formatSar(hold.amountMinor, lang)} · {formatWalletDate(hold.createdAt, lang)} → {formatWalletDate(hold.expiresAt, lang)}
              </div>
            ))}
          </div>
        )}

        {detail.topUps?.length > 0 && <h4>{text.topUpsTitle}</h4>}
        {detail.topUps?.length > 0 && (
          <div className="aiw-table-wrap">
            <table className="aiw-table">
              <thead>
                <tr>
                  <th>{text.colDate}</th>
                  <th>{text.invoice}</th>
                  <th className="num">{text.credit}</th>
                  <th className="num">{text.toPay}</th>
                  <th>{text.colStatus}</th>
                </tr>
              </thead>
              <tbody>
                {detail.topUps.map((topUp) => (
                  <tr key={topUp._id}>
                    <td>{formatWalletDate(topUp.createdAt, lang)}</td>
                    <td>{topUp.invoiceNumber || "-"}</td>
                    <td className="num">{formatSar(topUp.creditMinor, lang)}</td>
                    <td className="num">{formatSar(topUp.payableMinor, lang)}</td>
                    <td>
                      <span className={STATE_BADGE[topUp.state] || "badge-neutral"}>{text.states[topUp.state] || topUp.state}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <h4>{t.ledger}</h4>
        <div className="aiw-table-wrap">
          <table className="aiw-table">
            <thead>
              <tr>
                <th>{text.colDate}</th>
                <th>{text.colType}</th>
                <th className="num">{text.colAmount}</th>
                <th className="num">{text.colBalanceAfter}</th>
                <th>{text.colStatus}</th>
                <th>{t.colReason}</th>
                <th>{t.colActor}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {!entries.length && (
                <tr>
                  <td colSpan={8}>{text.empty}</td>
                </tr>
              )}
              {entries.map((entry) => (
                <tr key={entry._id}>
                  <td>{formatWalletDate(entry.postedAt || entry.createdAt, lang)}</td>
                  <td>
                    {text.entryTypes[entry.type] || entry.type}
                    <small dir="ltr">{entry.idempotencyKey}</small>
                  </td>
                  <td className="num" dir="ltr">
                    {entry.direction === "credit" ? "+" : "−"}
                    {formatSar(entry.amountMinor, lang)}
                    {entry.providerCostUsd !== null && entry.providerCostUsd !== undefined && <small>${entry.providerCostUsd}</small>}
                    {entry.uncoveredMinor > 0 && <small>{t.colShortfall}: {formatSar(entry.uncoveredMinor, lang)}</small>}
                  </td>
                  <td className="num">{entry.balanceAfterMinor === null || entry.balanceAfterMinor === undefined ? "-" : formatSar(entry.balanceAfterMinor, lang)}</td>
                  <td>
                    <span className={ENTRY_BADGE[entry.status] || "badge-neutral"}>{t.entryStatus[entry.status] || entry.status}</span>
                    {entry.voidReason && <small>{entry.voidReason}</small>}
                  </td>
                  <td>{entry.reason || "-"}</td>
                  <td>
                    {entry.actor?.name || "-"}
                    {entry.initiatedBy?.name && <small>{entry.initiatedBy.name}</small>}
                  </td>
                  <td>
                    {canAdjust && entry.status === "posted" && REVERSIBLE.has(entry.type) && (
                      <Button size="sm" appearance="outline" onClick={() => setAction({ kind: "reverse", entry })}>
                        {t.reverse}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {action && (
          <AiWalletActionModal
            title={action.kind === "adjust" ? t.adjust : action.kind === "reverse" ? `${t.reverse}: ${text.entryTypes[action.entry.type]} ${formatSar(action.entry.amountMinor, lang)}` : action.status === "frozen" ? t.freeze : t.unfreeze}
            fields={{ direction: action.kind === "adjust", amount: action.kind === "adjust", allowPartial: action.kind === "reverse" && action.entry.direction === "credit" }}
            text={text}
            lang={lang}
            onSubmit={runAction}
            onCancel={() => setAction(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="aiw-admin">
      <div className="aiw-toolbar">
        <Input label={t.search} value={search} onChange={(event) => setSearch(event.target.value)} />
        <label className="aiw-check">
          <input type="checkbox" checked={lowBalance} onChange={(event) => setLowBalance(event.target.checked)} />
          {t.lowBalanceOnly}
        </label>
        <Input label={t.storeIdPlaceholder} value={openId} onChange={(event) => setOpenId(event.target.value)} />
        <Button appearance="outline" disabled={!openId.trim()} onClick={() => loadDetail(openId.trim())}>
          {t.openWallet}
        </Button>
      </div>
      {error && <div className="billing-error">{error}</div>}
      {loading && !list && <div className="billing-loading">{text.loading}</div>}
      {list && !list.wallets.length ? (
        <div className="billing-empty-note">{t.noWallets}</div>
      ) : (
        list && (
          <div className="aiw-table-wrap">
            <table className="aiw-table">
              <thead>
                <tr>
                  <th>{t.colRestaurant}</th>
                  <th className="num">{t.colBalance}</th>
                  <th className="num">{t.colReserved}</th>
                  <th>{text.colStatus}</th>
                  <th>{t.colUpdated}</th>
                </tr>
              </thead>
              <tbody>
                {list.wallets.map((wallet) => (
                  <tr key={wallet._id} className="clickable" onClick={() => loadDetail(wallet.storeId)}>
                    <td>
                      {wallet.storeName || wallet.storeId}
                      <small>{wallet.storeId}</small>
                    </td>
                    <td className="num">
                      {formatSar(wallet.balanceMinor, lang)}
                      {wallet.balanceMinor < list.lowBalanceThresholdMinor && <small>{text.lowBalance}</small>}
                    </td>
                    <td className="num">{formatSar(wallet.reservedMinor, lang)}</td>
                    <td>
                      <span className={wallet.status === "frozen" ? "badge-danger" : "badge-success"}>{wallet.status === "frozen" ? text.frozen : t.ruleStatus.active}</span>
                    </td>
                    <td>{formatWalletDate(wallet.updatedAt, lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

export default AiWalletAdminWallets;
